import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { Button } from "../components/ui/button";
import {
  Package,
  CheckCircle2,
  Clock,
  Users,
  TrendingUp,
  ArrowUpRight,
  Truck,
  Plus,
  MapPin,
  BarChart3,
  ShieldCheck,
  Wifi,
  WifiOff,
  ClipboardList,
  Database,
  HardDrive,
  Layers,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import AgentPerksCard from "../components/AgentPerksCard";
import StatusBadge from "../components/StatusBadge";
import PriceCard from "../components/PriceCard";
import AnomalyAlertBanner from "../components/AnomalyAlertBanner";
import SupportHistoryCard from "../components/SupportHistoryCard";

// ===== USER Dashboard =====
function UserDashboard({ stats, recentOrders, loadingStats, navigate }) {
  const statCards = [
    { label: "Active Orders", value: stats?.active_orders ?? 0, icon: Package, color: "text-[#FF6B00]", bg: "bg-orange-50", filter: "active" },
    { label: "Completed", value: stats?.completed_deliveries ?? 0, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", filter: "completed" },
    { label: "Pending", value: stats?.pending_pickups ?? 0, icon: Clock, color: "text-slate-600", bg: "bg-slate-100", filter: "pending" },
  ];

  return (
    <>
      <div className="bg-gradient-to-r from-[#FF6B00] to-[#E65A00] rounded-2xl p-6 md:p-8 text-white animate-fadeInUp" data-testid="user-quick-action">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-['Outfit'] text-xl md:text-2xl font-bold">Book a Pickup</h2>
            <p className="text-orange-100 text-sm mt-1">Schedule a new delivery in under a minute</p>
          </div>
          <Button
            onClick={() => navigate("/dashboard/orders")}
            className="bg-white text-[#FF6B00] hover:bg-orange-50 rounded-xl px-6 py-3 font-semibold shadow-lg shadow-[#FF6B00]/20 btn-press h-auto"
            data-testid="book-pickup-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Pickup
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6" data-testid="user-stats-grid">
        {statCards.map((card, i) => (
          <StatCard
            key={card.label}
            card={card}
            loading={loadingStats}
            delay={i}
            onClick={() => navigate(`/dashboard/orders?filter=${card.filter}`)}
          />
        ))}
      </div>

      <RecentOrdersTable orders={recentOrders} navigate={navigate} />
      <SupportHistoryCard />
      <PlaceholderCards navigate={navigate} />
    </>
  );
}

// ===== AGENT Dashboard =====
function AgentDashboard({ user, stats, recentOrders, loadingStats, navigate }) {
  const [isOnline, setIsOnline] = useState(user?.is_online || false);
  const [toggling, setToggling] = useState(false);

  const toggleOnline = async () => {
    setToggling(true);
    try {
      await api.put("/agents/status", { is_online: !isOnline });
      setIsOnline(!isOnline);
      toast.success(isOnline ? "You're now offline" : "You're online — ready for deliveries!");
    } catch {
      toast.error("Failed to update status");
    } finally {
      setToggling(false);
    }
  };

  const statCards = [
    { label: "Assigned to Me", value: stats?.assigned_to_agent ?? 0, icon: ClipboardList, color: "text-blue-600", bg: "bg-blue-50", filter: "assigned" },
    { label: "Pending", value: stats?.pending_pickups ?? 0, icon: Clock, color: "text-slate-600", bg: "bg-slate-100", filter: "pending" },
    { label: "Delivered", value: stats?.completed_deliveries ?? 0, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50", filter: "delivered" },
  ];

  return (
    <>
      {!user?.is_verified && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5 md:p-6 animate-fadeInUp" data-testid="agent-pending-approval">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-lg font-bold text-amber-900">Pending Admin Approval</h3>
              <p className="text-sm text-amber-700 mt-1">Your agent account is under review. Once approved by the admin, you'll be able to go online and accept delivery orders.</p>
            </div>
          </div>
        </div>
      )}
      <div className={`rounded-2xl p-6 md:p-8 border transition-colors animate-fadeInUp ${isOnline ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`} data-testid="agent-status-banner">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${isOnline ? "bg-green-100" : "bg-slate-200"}`}>
              {isOnline ? <Wifi className="h-7 w-7 text-green-600" /> : <WifiOff className="h-7 w-7 text-slate-500" />}
            </div>
            <div>
              <h2 className="font-['Outfit'] text-xl md:text-2xl font-bold text-slate-900">
                {isOnline ? "You're Online" : "You're Offline"}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {isOnline ? "Ready to accept delivery assignments" : "Toggle online to start receiving orders"}
              </p>
            </div>
          </div>
          <Button
            onClick={toggleOnline}
            disabled={toggling}
            className={`rounded-xl px-6 py-3 font-semibold btn-press h-auto ${
              isOnline
                ? "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                : "bg-green-600 text-white hover:bg-green-700 shadow-sm shadow-green-600/20"
            }`}
            data-testid="agent-toggle-status-btn"
          >
            {isOnline ? "Go Offline" : "Go Online"}
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 animate-fadeInUp stagger-1" data-testid="agent-assigned-orders-cta">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-['Outfit'] text-base font-semibold text-slate-900">View Assigned Orders</p>
              <p className="text-xs text-slate-500">Check orders assigned to you for pickup and delivery</p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/dashboard/orders")}
            className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-5 py-2.5 font-medium shadow-sm shadow-[#FF6B00]/20 btn-press"
            data-testid="view-assigned-orders-btn"
          >
            View Orders
            <ArrowUpRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6" data-testid="agent-stats-grid">
        {statCards.map((card, i) => (
          <StatCard
            key={card.label}
            card={card}
            loading={loadingStats}
            delay={i}
            onClick={card.filter ? () => navigate(`/dashboard/agent/tasks?filter=${card.filter}`) : undefined}
          />
        ))}
      </div>

      {user?.is_verified && <AgentPerksCard />}

      <RecentOrdersTable orders={recentOrders} navigate={navigate} title="My Assignments" role="agent" />
    </>
  );
}

// ===== ADMIN Dashboard =====
function AdminDashboard({ stats, recentOrders, loadingStats, navigate }) {
  const statCards = [
    { label: "Total Orders", value: stats?.total_orders ?? 0, icon: Package, color: "text-[#FF6B00]", bg: "bg-orange-50" },
    { label: "Active Orders", value: stats?.active_orders ?? 0, icon: Truck, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Completed", value: stats?.completed_deliveries ?? 0, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
    { label: "Pending", value: stats?.pending_pickups ?? 0, icon: Clock, color: "text-slate-600", bg: "bg-slate-100" },
    { label: "Agents Online", value: `${stats?.agents_online ?? 0}/${stats?.agents_total ?? 0}`, icon: Users, color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Total Users", value: stats?.total_users ?? 0, icon: ShieldCheck, color: "text-teal-600", bg: "bg-teal-50" },
  ];

  return (
    <>
      <AnomalyAlertBanner role="admin" />
      <div className="bg-[#0B132B] rounded-2xl p-6 md:p-8 relative overflow-hidden animate-fadeInUp" data-testid="admin-overview-banner">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/15 to-transparent pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-5 w-5 text-orange-400" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">Platform Overview</span>
            </div>
            <h2 className="font-['Outfit'] text-xl md:text-2xl font-bold text-white">Admin Control Center</h2>
            <p className="text-slate-400 text-sm mt-1">Full visibility across orders, agents, and platform health</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/dashboard/agents")}
              className="bg-white/10 text-white hover:bg-white/20 border border-white/10 rounded-xl px-5 py-2.5 font-medium btn-press"
              data-testid="admin-manage-agents-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              Agents
            </Button>
            <Button
              onClick={() => navigate("/dashboard/orders")}
              className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-5 py-2.5 font-medium shadow-sm btn-press"
              data-testid="admin-view-orders-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Orders
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="admin-stats-grid">
        {statCards.map((card, i) => (
          <StatCard key={card.label} card={card} loading={loadingStats} delay={i} />
        ))}
      </div>

      <DBStatsCard />

      <RecentOrdersTable orders={recentOrders} navigate={navigate} title="All Recent Orders" />
      <PlaceholderCards navigate={navigate} />
    </>
  );
}

// ===== Database Storage Monitor (admin only) =====
function formatBytes(n) {
  if (!n || n < 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function DBStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/db-stats");
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load DB stats");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="db-stats-card">
        <div className="h-9 w-32 rounded-lg animate-shimmer mb-3" />
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-lg animate-shimmer" />)}
        </div>
      </div>
    );
  }
  if (!stats) return null;

  const fillPct = stats.free_tier_cap_bytes > 0
    ? Math.min(100, (stats.storage_size / stats.free_tier_cap_bytes) * 100)
    : 0;
  const fillColor = fillPct > 80 ? "bg-red-500" : fillPct > 50 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 animate-fadeInUp" data-testid="db-stats-card">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Database className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-['Outfit'] font-bold text-slate-900">Database Storage</h3>
            <p className="text-xs text-slate-500">Live MongoDB usage — admin-only</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold px-3 py-1.5 rounded-lg border border-indigo-100 hover:bg-indigo-50 transition disabled:opacity-50"
          data-testid="db-stats-refresh-btn"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="rounded-xl border border-slate-100 p-3" data-testid="db-data-size">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            <Database className="h-3 w-3" />
            Data size
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{formatBytes(stats.data_size)}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">Uncompressed user data</p>
        </div>
        <div className="rounded-xl border border-slate-100 p-3" data-testid="db-storage-size">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            <HardDrive className="h-3 w-3" />
            Storage size
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{formatBytes(stats.storage_size)}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">On-disk (compressed) + indexes {formatBytes(stats.index_size)}</p>
        </div>
        <div className="rounded-xl border border-slate-100 p-3" data-testid="db-collection-count">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            <Layers className="h-3 w-3" />
            Collections
          </div>
          <p className="font-['Outfit'] text-2xl font-bold text-slate-900">{stats.total_collections}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{stats.total_documents.toLocaleString()} total documents</p>
        </div>
      </div>

      {/* Free-tier fill bar — heuristic against M0 (512 MB) */}
      <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 mb-4" data-testid="db-fill-bar">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="font-semibold text-slate-600">vs Atlas M0 free-tier (512 MB)</span>
          <span className="font-mono font-bold text-slate-900">{fillPct.toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
          <div className={`h-full ${fillColor} transition-all duration-500`} style={{ width: `${fillPct}%` }} />
        </div>
      </div>

      {/* Per-collection breakdown — top 10 */}
      <div data-testid="db-collections-table">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Top collections by document count</p>
        <div className="rounded-xl border border-slate-100 divide-y divide-slate-100 max-h-80 overflow-auto">
          {stats.collections.slice(0, 10).map((c) => (
            <div key={c.name} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`db-coll-${c.name}`}>
              <span className="font-mono text-slate-700 truncate flex-1">{c.name}</span>
              <span className="text-slate-500 text-xs mr-3">{c.count.toLocaleString()} docs</span>
              <span className="text-slate-700 text-xs font-semibold w-20 text-right">{formatBytes(c.storage_size)}</span>
            </div>
          ))}
        </div>
        {stats.collections.length > 10 && (
          <p className="text-[11px] text-slate-400 mt-2 text-center">
            +{stats.collections.length - 10} more smaller collections
          </p>
        )}
      </div>
    </div>
  );
}

// ===== Shared Components =====
function StatCard({ card, loading, delay = 0, onClick }) {
  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      aria-label={clickable ? `View ${card.label.toLowerCase()}` : undefined}
      className={`text-left w-full bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6 card-hover animate-fadeInUp ${clickable ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-[#FF6B00]/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/40" : ""}`}
      style={{ animationDelay: `${delay * 0.06}s` }}
      data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 ${card.bg} rounded-xl flex items-center justify-center`}>
          <card.icon className={`h-5 w-5 ${card.color}`} />
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-green-600 uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 live-dot" />
          Live
        </div>
      </div>
      {loading ? (
        <div className="h-9 w-16 rounded-lg animate-shimmer" />
      ) : (
        <p className="font-['Outfit'] text-3xl font-bold text-slate-900 tracking-tight">{card.value}</p>
      )}
      <p className="text-sm text-slate-500 mt-1">{card.label}</p>
    </Tag>
  );
}

function RecentOrdersTable({ orders, navigate, title = "Recent Orders", role = "admin" }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="recent-orders-section">
      <div className="flex items-center justify-between p-5 md:p-6 border-b border-slate-100">
        <div>
          <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">{title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">Latest delivery activity</p>
        </div>
        <button
          onClick={() => navigate("/dashboard/orders")}
          className="flex items-center gap-1 text-sm text-[#FF6B00] font-semibold cursor-pointer hover:text-[#E65A00] transition-colors"
          data-testid="view-all-orders"
        >
          View all
          <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="p-12 text-center" data-testid="no-orders-message">
          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Truck className="h-7 w-7 text-[#FF6B00]/40" />
          </div>
          <p className="text-base font-semibold text-slate-700">No orders yet</p>
          <p className="text-sm text-slate-400 mt-1">Book your first pickup and get it delivered fast!</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-100 bg-slate-50/40">
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-6 py-3">Tracking ID</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Package</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hidden md:table-cell py-3">From / To</TableHead>
                {role !== "agent" && <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Agent</TableHead>}
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Price</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 py-3">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.tracking_id} className="border-slate-50 row-hover" data-testid={`order-row-${order.tracking_id}`}>
                  <TableCell className="pl-6 py-3">
                    <Link
                      to={`/dashboard/orders/${order.tracking_id}`}
                      className="text-sm font-mono font-semibold text-[#FF6B00] hover:underline"
                      data-testid={`recent-order-track-${order.tracking_id}`}
                    >
                      {order.tracking_id}
                    </Link>
                  </TableCell>
                  <TableCell className="py-3">
                    <span className="text-sm text-slate-700">{order.package_description}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell py-3">
                    <div className="text-xs text-slate-500 max-w-[180px] space-y-0.5">
                      <div className="truncate">{order.pickup_address}</div>
                      <div className="truncate text-slate-400">{order.delivery_address}</div>
                    </div>
                  </TableCell>
                  {role !== "agent" && (
                    <TableCell className="py-3">
                      {order.agent_name
                        ? <span className="text-sm text-slate-700">{order.agent_name}</span>
                        : <span className="text-xs text-slate-400 italic">Unassigned</span>
                      }
                    </TableCell>
                  )}
                  <TableCell className="py-3">
                    {order.price ? (
                      <PriceCard price={order.price} compact />
                    ) : (
                      <span className="text-xs text-slate-400">--</span>
                    )}
                  </TableCell>
                  <TableCell className="py-3">
                    <StatusBadge status={order.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function PlaceholderCards() {
  // Sprint-104d (interim) · two legacy placeholders replaced with real
  // data-backed widgets (Recent Orders + Account Summary). Full Live
  // Tracking Map / Payment Overview widgets pending fresh-session build.
  const nav = useNavigate();
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.allSettled([
          api.get("/dashboard/recent-orders?limit=3"),
          api.get("/dashboard/stats"),
        ]);
        if (r1.status === "fulfilled")
          setRecent(r1.value.data?.orders || r1.value.data?.rows || []);
        if (r2.status === "fulfilled") setStats(r2.value.data || {});
      } catch { /* silent */ }
    })();
  }, []);
  return (
    <div className="grid md:grid-cols-2 gap-4 md:gap-6 animate-fadeInUp">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 card-hover"
           data-testid="recent-orders-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Recent Orders</h3>
          <button onClick={() => nav("/dashboard/orders")}
                  className="text-xs text-orange-600 hover:underline"
                  data-testid="recent-orders-view-all">View all →</button>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No orders yet.</p>
        ) : (
          <ul className="space-y-2">
            {recent.slice(0, 3).map((o) => (
              <li key={o.tracking_id}
                  onClick={() => nav(`/dashboard/orders/${o.tracking_id}`)}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                  data-testid={`recent-order-${o.tracking_id}`}>
                <div className="min-w-0">
                  <div className="font-mono text-xs text-slate-800">{o.tracking_id}</div>
                  <div className="text-xs text-slate-500 truncate">{o.delivery_address || o.pickup_address || "—"}</div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 shrink-0">{o.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 card-hover"
           data-testid="wallet-summary-card">
        <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4">Account Summary</h3>
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Total Orders" value={stats?.total_orders ?? "—"} />
          <Stat label="Delivered" value={stats?.delivered_count ?? stats?.completed_orders ?? "—"} />
          <Stat label="In Transit" value={stats?.in_transit_count ?? "—"} />
          <Stat label="Saved Addresses" value={stats?.saved_addresses ?? stats?.address_count ?? "—"} />
        </div>
        <button onClick={() => nav("/dashboard/wallet")}
                className="mt-4 w-full text-sm bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-xl py-2 font-medium"
                data-testid="wallet-cta">Open Wallet →</button>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-lg font-bold text-slate-800 font-['Outfit']">{value}</div>
    </div>
  );
}

// ===== Main Dashboard =====
export default function Dashboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const role = user?.role || "user";
  const hierarchyRedirect =
    role === "founder" ? "/dashboard/founder" :
    role === "ceo" ? "/dashboard/ceo" :
    role === "main_hub_manager" ? "/dashboard/hub" :
    role === "micro_hub_lead" ? "/dashboard/micro-hub" :
    role === "packer_staff" ? (user?.approval_status === "active" ? "/packer/dashboard" : "/packer/pending") :
    role === "ops_staff" ? "/dashboard/ops" : null;

  useEffect(() => {
    if (hierarchyRedirect) return;
    const fetchData = async () => {
      try {
        const [statsRes, ordersRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/recent-orders"),
        ]);
        setStats(statsRes.data);
        setRecentOrders(ordersRes.data);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchData();
  }, [hierarchyRedirect]);

  // Sprint 70 — redirect hierarchy roles to their own dashboards
  if (hierarchyRedirect) return <Navigate to={hierarchyRedirect} replace />;

  const roleLabel = role === "admin" ? "Admin" : role === "agent" ? "Agent" : "User";

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="dashboard-greeting">
            Welcome back, {user?.name || "User"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Here's what's happening with your deliveries today.
          </p>
        </div>
        <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold self-start sm:self-auto border ${
          role === "admin" ? "bg-violet-50 text-violet-700 border-violet-200" :
          role === "agent" ? "bg-teal-50 text-teal-700 border-teal-200" :
         "bg-orange-50 text-[#FF6B00] border-orange-200"
        }`} data-testid="role-badge">
          {roleLabel}
        </span>
      </div>

      {role === "admin" && <AdminDashboard stats={stats} recentOrders={recentOrders} loadingStats={loadingStats} navigate={navigate} />}
      {role === "agent" && <AgentDashboard user={user} stats={stats} recentOrders={recentOrders} loadingStats={loadingStats} navigate={navigate} />}
      {role === "user" && <UserDashboard stats={stats} recentOrders={recentOrders} loadingStats={loadingStats} navigate={navigate} />}
    </div>
  );
}
